import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { completeLogin, login } from '@/auth/keycloak'
import { configurationProblems } from '@/config'
import { Role, useAuthStore } from '@/stores/auth'

/** Sidebar sections, in the order they are drawn; '' is the dashboard on its own. */
export const NAV_SECTIONS = ['', 'devices', 'rental', 'organization', 'distribution', 'platform'] as const
export type NavSection = (typeof NAV_SECTIONS)[number]

declare module 'vue-router' {
  interface RouteMeta {
    /** Reachable without signing in: the public site, the OIDC callback. */
    public?: boolean
    /**
     * The frame the screen renders in: the console shell (default), the public
     * site's header and footer, or nothing at all.
     */
    layout?: 'console' | 'public' | 'none'
    /** Reachable by link but not listed in the sidebar. */
    hidden?: boolean
    /** i18n key of the sidebar label and the breadcrumb. */
    titleKey?: string
    icon?: string
    section?: NavSection
    /** Console roles (`ROLE_MDM_*`) of which the user needs at least one. */
    roles?: string[]
  }
}

/**
 * The console's screens, plus the OIDC callback.
 *
 * Paths follow web-app's (`/dashboard`, `/devices`, …) so bookmarks and printed
 * links keep working after the move; `/` belongs to the public landing page.
 *
 * `meta.roles` hides a route from users who may not use it. The server refuses the
 * underlying calls regardless — this only keeps the navigation honest.
 */
const routes: RouteRecordRaw[] = [
  {
    path: '/auth/callback',
    name: 'auth-callback',
    // Not a screen: it consumes the authorization code and redirects onward.
    component: () => import('@/views/AuthCallbackView.vue'),
    meta: { public: true, layout: 'none' },
  },
  {
    // Where a login that could not even begin ends up. Public and without
    // navigation for the same reason the callback is: there is no session yet.
    path: '/auth/error',
    name: 'auth-error',
    component: () => import('@/views/AuthErrorView.vue'),
    meta: { public: true, layout: 'none', hidden: true },
  },
  // ------------------------------------------------------------ public site
  // web-app's public pages, same paths. No sign-in: a locked phone has no session,
  // and its web filter reaches nothing but this origin.
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/public/HomeView.vue'),
    meta: { public: true, layout: 'public', hidden: true },
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: () => import('@/views/public/LegalView.vue'),
    props: { kind: 'privacy' },
    meta: { public: true, layout: 'public', hidden: true },
  },
  {
    path: '/terms',
    name: 'terms',
    component: () => import('@/views/public/LegalView.vue'),
    props: { kind: 'terms' },
    meta: { public: true, layout: 'public', hidden: true },
  },
  {
    path: '/support',
    name: 'support',
    component: () => import('@/views/public/SupportView.vue'),
    meta: { public: true, layout: 'public', hidden: true },
  },
  {
    path: '/enroll/:token',
    name: 'enroll',
    component: () => import('@/views/public/EnrollView.vue'),
    meta: { public: true, layout: 'public', hidden: true },
  },
  {
    // The kiosk web clip of the lockout profile; before /client/:id, which would take it.
    path: '/client/lockout',
    name: 'client-lockout',
    component: () => import('@/views/public/LockoutView.vue'),
    meta: { public: true, layout: 'none', hidden: true },
  },
  {
    path: '/client/:id',
    name: 'client',
    component: () => import('@/views/public/DeviceLockView.vue'),
    meta: { public: true, layout: 'none', hidden: true },
  },
  {
    path: '/lock/:id',
    name: 'lock',
    component: () => import('@/views/public/DeviceLockView.vue'),
    meta: { public: true, layout: 'none', hidden: true },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/public/LoginView.vue'),
    meta: { public: true, layout: 'none', hidden: true },
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { titleKey: 'nav.dashboard', icon: 'pi pi-home' },
  },
  {
    path: '/devices',
    name: 'devices',
    component: () => import('@/views/devices/DevicesView.vue'),
    meta: { titleKey: 'nav.devices', section: 'devices', icon: 'pi pi-mobile' },
  },
  {
    // A full page rather than a drawer: the device has too many panels for one (PORT-PLAN.md).
    path: '/devices/:id',
    name: 'device',
    component: () => import('@/views/devices/DeviceView.vue'),
    meta: { titleKey: 'nav.device', section: 'devices', hidden: true },
  },
  {
    path: '/locations',
    name: 'locations',
    component: () => import('@/views/devices/LocationsView.vue'),
    meta: { titleKey: 'nav.locations', section: 'devices', icon: 'pi pi-map-marker' },
  },
  {
    path: '/customers',
    name: 'customers',
    component: () => import('@/views/rental/CustomersView.vue'),
    meta: { titleKey: 'nav.customers', section: 'rental', icon: 'pi pi-users' },
  },
  {
    path: '/contracts',
    name: 'contracts',
    component: () => import('@/views/rental/ContractsView.vue'),
    meta: { titleKey: 'nav.contracts', section: 'rental', icon: 'pi pi-file' },
  },
  {
    path: '/payments',
    name: 'payments',
    component: () => import('@/views/rental/PaymentsView.vue'),
    meta: { titleKey: 'nav.payments', section: 'rental', icon: 'pi pi-wallet' },
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/views/organization/SettingsView.vue'),
    meta: {
      titleKey: 'nav.settings',
      section: 'organization',
      icon: 'pi pi-cog',
      roles: [Role.ORG_ADMIN, Role.PLATFORM_ADMIN, Role.PLATFORM_MANAGER],
    },
  },
  {
    path: '/employees',
    name: 'employees',
    component: () => import('@/views/organization/EmployeesView.vue'),
    meta: {
      titleKey: 'nav.employees',
      section: 'organization',
      icon: 'pi pi-id-card',
      roles: [Role.ORG_ADMIN, Role.PLATFORM_ADMIN, Role.PLATFORM_MANAGER],
    },
  },
  {
    path: '/balance',
    name: 'balance',
    component: () => import('@/views/organization/BalanceView.vue'),
    meta: {
      titleKey: 'nav.balance',
      section: 'organization',
      icon: 'pi pi-dollar',
      roles: [Role.ORG_ADMIN, Role.PLATFORM_ADMIN, Role.PLATFORM_MANAGER],
    },
  },
  {
    path: '/distributor/organizations',
    name: 'distributor-organizations',
    component: () => import('@/views/distributor/DistributorOrganizationsView.vue'),
    meta: {
      titleKey: 'nav.distributorOrganizations',
      section: 'distribution',
      icon: 'pi pi-building',
      roles: [Role.DISTRIBUTOR],
    },
  },
  {
    path: '/distributor/balance',
    name: 'distributor-balance',
    component: () => import('@/views/distributor/DistributorBalanceView.vue'),
    meta: {
      titleKey: 'nav.distributorBalance',
      section: 'distribution',
      icon: 'pi pi-wallet',
      roles: [Role.DISTRIBUTOR],
    },
  },
  {
    path: '/platform/organizations',
    name: 'platform-organizations',
    component: () => import('@/views/platform/PlatformOrganizationsView.vue'),
    meta: {
      titleKey: 'nav.platformOrganizations',
      section: 'platform',
      icon: 'pi pi-building',
      roles: [Role.PLATFORM_ADMIN, Role.PLATFORM_MANAGER],
    },
  },
  {
    path: '/platform/distributors',
    name: 'platform-distributors',
    component: () => import('@/views/platform/PlatformDistributorsView.vue'),
    meta: {
      titleKey: 'nav.platformDistributors',
      section: 'platform',
      icon: 'pi pi-share-alt',
      roles: [Role.PLATFORM_ADMIN],
    },
  },
  {
    path: '/platform/applications',
    name: 'platform-applications',
    component: () => import('@/views/platform/PlatformApplicationsView.vue'),
    meta: {
      titleKey: 'nav.platformApplications',
      section: 'platform',
      icon: 'pi pi-inbox',
      roles: [Role.PLATFORM_ADMIN, Role.PLATFORM_MANAGER],
    },
  },
  {
    // APNs push certificate, root CA and profile signing: one set for the platform.
    path: '/platform/apple',
    name: 'platform-apple',
    component: () => import('@/views/platform/PlatformAppleView.vue'),
    meta: {
      titleKey: 'nav.platformApple',
      section: 'platform',
      icon: 'pi pi-apple',
      roles: [Role.PLATFORM_ADMIN],
    },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/views/ProfileView.vue'),
    meta: { titleKey: 'nav.profile', icon: 'pi pi-user', hidden: true },
  },
  {
    path: '/forbidden',
    name: 'forbidden',
    component: () => import('@/views/ForbiddenView.vue'),
    meta: { hidden: true },
  },
  {
    // Signed in, holding no MDM role. Rendered without navigation: every screen
    // behind it answers 403, so offering the menu would only lead to a set of empty
    // tables that look like missing data.
    path: '/no-role',
    name: 'no-role',
    component: () => import('@/views/NoRoleView.vue'),
    meta: { layout: 'none', hidden: true },
  },
  {
    // Signed in with a role, but mdm-service could not resolve an organization for
    // the session (none in the token, or a suspended one). Same reasoning as /no-role.
    path: '/no-organization',
    name: 'no-organization',
    component: () => import('@/views/NoOrganizationView.vue'),
    meta: { layout: 'none', hidden: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { hidden: true },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  // In-page anchors of the landing page (/#employee-intake), and the top of every other screen.
  scrollBehavior(to) {
    return to.hash ? { el: to.hash, behavior: 'smooth' } : { top: 0 }
  },
})

router.beforeEach(async (to) => {
  if (to.meta.public) {
    return true
  }

  const auth = useAuthStore()
  if (auth.loading) {
    await auth.load()
  }

  if (!auth.authenticated) {
    // A bundle that names no Keycloak, or an origin the browser will not give
    // crypto.subtle to, cannot start a login at all. Say so on a screen instead
    // of throwing out of the guard, which aborts the navigation and leaves the
    // operator looking at a blank page with nothing in the network tab.
    const problems = configurationProblems()
    if (problems.length > 0) {
      return { name: 'auth-error', query: { reason: problems.join(' ') } }
    }
    try {
      // Remember where they were going; completeLogin returns it.
      await login(to.fullPath)
      return false
    } catch (error) {
      return {
        name: 'auth-error',
        query: { reason: error instanceof Error ? error.message : String(error) },
      }
    }
  }

  // Authenticated and role-less. The server refuses every admin call from such a
  // token, so the console says so once instead of rendering screens that each come
  // back empty.
  if (!auth.hasAnyPanelRole) {
    return to.name === 'no-role' ? true : { name: 'no-role' }
  }

  // A distributor works in no organization: its cabinet is the whole console for it, and the
  // cabinet is where a sign-in lands.
  if (auth.isDistributorOnly) {
    const own = to.meta.roles?.includes(Role.DISTRIBUTOR) || to.name === 'profile' || to.name === 'forbidden'
    return own ? true : { name: 'distributor-organizations' }
  }

  // Every console screen except the platform ones works in an organization. Platform staff without one can
  // still pick an organization on the profile screen.
  if (!auth.hasOrganization && !auth.isPlatformStaff) {
    return to.name === 'no-organization' ? true : { name: 'no-organization' }
  }

  const required = to.meta.roles
  if (required && !auth.hasRole(...required)) {
    return { name: 'forbidden' }
  }
  return true
})

export { completeLogin }
